import { useMemo } from 'react';
import { I18n } from '@aws-amplify/core';

interface Review {
	id: string;
	name: string;
	files: Array<{ id: string; original_name: string }>;
	created_at: string;
	updated_at: string;
}

interface UseModalReviewsProps {
	data: Review[];
}

interface VersionedReview {
	id: string;
	name: string;
	creation_date: string;
	file_id: string;
	file: {
		id: string;
		original_name: string;
	};
}

export function useModalReviews({ data }: UseModalReviewsProps): VersionedReview[] {
	return useMemo(() => {
		const sortedReviews = sortByDate(data);

		return mapToVersionedFormat(sortedReviews);
	}, [data]);
}

export function useLastReviewId({ data }: UseModalReviewsProps): string | null {
	return useMemo(() => {
		if (!data || data.length === 0) return null;

		const sortedReviews = sortByDate(data);
		return sortedReviews[sortedReviews.length - 1]?.id || null;
	}, [data]);
}

function sortByDate(reviews: Review[]): Review[] {
	return reviews.sort((first, second) => {
		const firstDate = getDate(first);
		const secondDate = getDate(second);
		return new Date(firstDate).getTime() - new Date(secondDate).getTime();
	});
}

function getDate(review: Review): string {
	return review.updated_at || review.created_at;
}

function mapToVersionedFormat(reviews: Review[]): VersionedReview[] {
	return reviews.map((review, index) => {
		const date = getDate(review);
		const fileIds = joinFileIds(review.files);
		const fileNames = joinFileNames(review.files);

		return {
			id: review.id,
			name: index === 0 ? I18n.get('Initial version') : `${I18n.get('Version')} ${index + 1}`,
			creation_date: I18n.get(date),
			file_id: fileIds,
			file: {
				id: fileIds,
				original_name: fileNames
			}
		};
	});
}

function joinFileIds(files: Array<{ id: string; original_name: string }>): string {
	return files.map((file) => file.id).join(',');
}

function joinFileNames(files: Array<{ id: string; original_name: string }>): string {
	return files.map((file) => file.original_name).join(', ');
}
